import { Show, createSignal, createEffect, onCleanup, on } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { useQueryClient } from "@tanstack/solid-query";
import { organization as orgClient, invalidateOrgList } from "../lib/auth-client";
import { apiClient, formatApiError } from "../lib/api-client";
import { Text, useI18n } from "./i18n";
import { notifications } from "./Notifications";
import { SimpleDialog } from "./SimpleDialog";
import { SlugInput, useSlugCheck, normalizeSlug } from "./SlugInput";
import { uploadOrgLogo } from "./uploadOrgLogo";

interface CreateOrganizationDialogProps {
  open: boolean;
  onClose: () => void;
  /** Admin mode: shows owner email field and uses different API */
  adminMode?: boolean;
  /** Called after successful creation in admin mode */
  onAdminCreateSuccess?: () => void;
}

export const CreateOrganizationDialog = (props: CreateOrganizationDialogProps) => {
  const i18n = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = createSignal("");
  const [slug, setSlug] = createSignal("");
  const [isSlugEdited, setIsSlugEdited] = createSignal(false);
  const [logoFile, setLogoFile] = createSignal<File | null>(null);
  const [logoPreview, setLogoPreview] = createSignal<string | null>(null);
  const [ownerEmail, setOwnerEmail] = createSignal("");
  const [loading, setLoading] = createSignal(false);

  const slugStatus = useSlugCheck(slug);

  // Auto-generate slug from name
  createEffect(() => {
    if (!isSlugEdited()) {
      setSlug(normalizeSlug(name()));
    }
  });

  // Reset form when dialog opens (only track props.open to avoid re-running on signal changes)
  createEffect(
    on(
      () => props.open,
      (open) => {
        if (open) {
          setName("");
          setSlug("");
          setIsSlugEdited(false);
          setLogoFile(null);
          setLogoPreview(null);
          setOwnerEmail("");
        }
      }
    )
  );

  onCleanup(() => revokeLogoPreview());

  function revokeLogoPreview() {
    const prev = logoPreview();
    if (prev) URL.revokeObjectURL(prev);
  }

  const handleLogoChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    if (target.files && target.files[0]) {
      revokeLogoPreview();
      const file = target.files[0];
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleCreate = async (e: Event) => {
    e.preventDefault();
    if (!name() || !slug() || slugStatus() === "taken") return;
    if (props.adminMode && !ownerEmail()) return;
    setLoading(true);

    const pendingLogoFile = logoFile();

    if (props.adminMode) {
      const createPromise = apiClient.organizations
        .$post({
          json: {
            name: name(),
            slug: slug(),
            ownerEmail: ownerEmail(),
          },
        })
        .then(async (response) => {
          if (!response.ok) {
            const data = await response.json();
            throw new Error("error" in data ? formatApiError(data.error) : "Failed to create organization");
          }
          const data = (await response.json()) as { organization: { id: string } };
          // Upload logo if a file was selected
          if (pendingLogoFile) {
            const logoUrl = await uploadOrgLogo(pendingLogoFile, data.organization.id);
            await apiClient.organizations[":id"].$put({
              param: { id: data.organization.id },
              json: { logo: logoUrl },
            });
          }
          return data;
        });

      notifications.promise(createPromise, {
        loading: i18n.t("Creating organization..."),
        success: i18n.t("Organization created successfully"),
        error: (error: Error) => error.message,
      });

      createPromise
        .then(() => {
          props.onClose();
          props.onAdminCreateSuccess?.();
          invalidateOrgList();
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      // Normal mode: use Better Auth client
      const createPromise = orgClient.create(
        {
          name: name(),
          slug: slug(),
        },
        {
          onSuccess: async (ctx) => {
            // Upload logo if a file was selected
            if (pendingLogoFile) {
              const logoUrl = await uploadOrgLogo(pendingLogoFile, ctx.data.id);
              await orgClient.update({ data: { logo: logoUrl } });
            }
            props.onClose();
            queryClient.invalidateQueries({ queryKey: ["admin-organizations"] });
            // Navigate to the new organization
            navigate(`/${ctx.data.slug}`);
          },
          onError: () => {
            setLoading(false);
          },
        }
      );

      notifications.promise(createPromise, {
        loading: i18n.t("Creating organization..."),
        success: i18n.t("Organization created successfully"),
        error: (error) => error.error.message,
      });
    }
  };

  const isFormValid = () => {
    const baseValid = name() && slug() && slugStatus() !== "taken" && slugStatus() !== "checking";
    if (props.adminMode) {
      return baseValid && ownerEmail();
    }
    return baseValid;
  };

  return (
    <SimpleDialog isOpen={props.open} onClose={props.onClose} title={<Text message="Create Organization" />}>
      <form onSubmit={handleCreate}>
        <div class="flex flex-col gap-4">
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium">
              <Text message="Name" />
            </span>
            <input
              type="text"
              class="ui-input w-full"
              value={name()}
              onInput={(e) => setName(e.currentTarget.value)}
              placeholder={i18n.t("Name")}
              required
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium">
              <Text message="Slug" />
            </span>
            <SlugInput
              value={slug()}
              onInput={(value) => {
                setSlug(value);
                setIsSlugEdited(true);
              }}
              slugStatus={slugStatus()}
              placeholder={i18n.t("Slug")}
              class="w-full"
            />
          </label>
          <Show when={props.adminMode}>
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium">
                <Text message="Owner Email" />
              </span>
              <input
                type="email"
                class="ui-input w-full"
                value={ownerEmail()}
                onInput={(e) => setOwnerEmail(e.currentTarget.value)}
                placeholder={i18n.t("Owner Email")}
                required
              />
              <span class="text-base-content/70 text-xs">
                <Text message="The user with this email will be the owner of the organization" />
              </span>
            </label>
          </Show>
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium">
              <Text message="Logo" />
            </span>
            <input type="file" class="ui-file-input w-full" accept="image/*" onChange={handleLogoChange} />
            <Show when={logoPreview()}>
              <div class="mt-2 flex items-center gap-2">
                <img src={logoPreview()!} alt="Logo preview" class="rounded-box h-16 w-16 object-cover" />
                <button
                  type="button"
                  class="ui-btn ui-btn-soft ui-btn-sm ui-btn-error"
                  onClick={() => {
                    revokeLogoPreview();
                    setLogoFile(null);
                    setLogoPreview(null);
                  }}
                >
                  <Text message="Delete" />
                </button>
              </div>
            </Show>
          </div>
        </div>
        <div class="ui-modal-action">
          <button type="button" class="ui-btn ui-btn-secondary" onClick={() => props.onClose()}>
            <Text message="Cancel" />
          </button>
          <button type="submit" class="ui-btn ui-btn-primary" disabled={!isFormValid() || loading()}>
            <Show when={loading()} fallback={<Text message="Create" />}>
              <span class="ui-loading ui-loading-spinner ui-loading-sm" />
            </Show>
          </button>
        </div>
      </form>
    </SimpleDialog>
  );
};
